import React, { useState } from 'react';
import {
    Box,
    Chip,
    Collapse,
    IconButton,
    Paper,
    Typography,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

/**
 * Collapsible action inbox — KPI-first dashboards show a compact strip by default;
 * full work-queue tables expand on demand (design-review work-queue / KPI order).
 *
 * @param {object} props
 * @param {string} props.title - Inbox heading
 * @param {string} [props.subtitle] - One-line description
 * @param {{ total: number, high?: number }} [props.stats] - Item counts for collapsed summary
 * @param {boolean} [props.loading] - Loading state for summary chip
 * @param {boolean} [props.defaultExpanded] - Initial expand state
 * @param {string} [props.testId] - data-testid / data-cy root
 * @param {React.ReactNode} props.children - Expanded queue content
 */
const DashboardActionInbox = ({
    title,
    subtitle = null,
    stats = null,
    loading = false,
    defaultExpanded = false,
    testId = 'dashboard-action-inbox',
    children,
}) => {
    const [expanded, setExpanded] = useState(defaultExpanded);

    const total = stats?.total ?? 0;
    const highCount = stats?.high ?? 0;

    const summaryLabel = loading
        ? 'Loading…'
        : total === 0
            ? 'No open items'
            : `${total} item${total === 1 ? '' : 's'}`;

    const toggleExpanded = () => setExpanded((prev) => !prev);

    return (
        <Paper
            variant="outlined"
            sx={{ mb: 3, overflow: 'hidden' }}
            data-cy={testId}
            data-testid={testId}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 1,
                    px: 2,
                    py: 1.5,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                }}
                onClick={toggleExpanded}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        toggleExpanded();
                    }
                }}
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                aria-label={`${title}. ${summaryLabel}. ${expanded ? 'Collapse' : 'Expand'} queue.`}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" component="h2">
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography variant="body2" color="text.secondary" noWrap={!expanded}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    {!loading && total > 0 && highCount > 0 && (
                        <Chip size="small" label={`${highCount} high`} color="error" />
                    )}
                    <Chip
                        size="small"
                        label={summaryLabel}
                        color={!loading && total > 0 ? 'warning' : 'default'}
                        variant={total > 0 ? 'filled' : 'outlined'}
                    />
                    <IconButton
                        size="small"
                        aria-hidden
                        tabIndex={-1}
                        onClick={(event) => {
                            event.stopPropagation();
                            toggleExpanded();
                        }}
                    >
                        {expanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                </Box>
            </Box>
            <Collapse in={expanded}>
                <Box
                    sx={{
                        px: 2,
                        pb: 2,
                        borderTop: 1,
                        borderColor: 'divider',
                    }}
                >
                    {children}
                </Box>
            </Collapse>
        </Paper>
    );
};

export default DashboardActionInbox;
